import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { appInsights } from '../services/ApplicationInsightsService';
import { types } from '../actions/timesheet';

const initialState = {
  pending: false,
  commentPending: false,
  error: '',
  isAccessDenied: false,
  data: null,
  tandecounts: null,
  contractortandecounts: null,
  approverRequest: null,
  notifications: [
    { type: 'contractor', data: null },
    { type: 'approver', data: null }
  ],
  validatePending: false,
  validate: {
    timesheet: false,
    expense: false
  },
  saveSuccess: false,
  submitSuccess: false,
  selectedSectionData: null
};

export default function reduce(state = initialState, action) {
  switch (action.type) {
    case types.GET_PARTICULAR_TIMESHEET_PENDING:
      return { ...state, data: null, pending: true, error: '', isAccessDenied: false };
    case types.GET_PARTICULAR_TIMESHEET_REJECTED:
      appInsights.trackException({
        error: new Error(action.payload?.response?.data || action.payload.message),
        severityLevel: SeverityLevel.Error
      });
      return {
        ...state,
        pending: false,
        error: action.payload.message,
        isAccessDenied: action.payload?.response?.status === 403
      };
    case types.GET_PARTICULAR_TIMESHEET_FULFILLED:
      return { ...state, pending: false, error: '', data: action.payload, isAccessDenied: false };
    case types.UPDATE_GENERAL_DATA:
      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          [action.payload.Data.prop]: action.payload.Data.value
        }
      };
    case types.UPDATE_TIMESHEET: {
      const { Data, SectionId, IsSplitApprovalFlow } = action.payload;
      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          ...(IsSplitApprovalFlow
            ? {
                TimesheetSections: state?.data?.TimesheetSections?.map((section) => {
                  if (section.Id !== SectionId) return section;

                  if (Data.Id === SectionId) {
                    return {
                      ...section,
                      ...Data,
                      TimesheetDetails: Data.TimesheetDetails || section.TimesheetDetails,
                      TimesheetAttachments: Data.TimesheetAttachments || section.TimesheetAttachments
                    };
                  }

                  return {
                    ...section,
                    TimesheetDetails: section.TimesheetDetails?.map((timesheet) =>
                      timesheet.Id === Data.Id ? { ...timesheet, ...Data } : timesheet
                    )
                  };
                })
              }
            : {
                TimesheetDetails: state?.data?.TimesheetDetails.map((timesheet) =>
                  timesheet.Id === Data.Id ? Data : timesheet
                )
              })
        }
      };
    }
    case types.UPDATE_ATTACHMENTS:
      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          GlobalFiles: [
            ...(state.data.GlobalFiles || []),
            { SectionId: action.payload.SectionId, File: action.payload.File }
          ],
          TimesheetSections: state.data.TimesheetSections.map((section) => {
            if (section.Id !== action.payload.SectionId) return section;

            return {
              ...section,
              TimesheetAttachments: [...(section.TimesheetAttachments || []), action.payload.TempData]
            };
          })
        }
      };
    case types.DELETE_TIMESHEET_LINE:
      if (action.payload.isSplitApprovalFlow) {
        return {
          ...state,
          pending: false,
          data: {
            ...state.data,
            TimesheetSections: state.data.TimesheetSections.map((section) => {
              if (section.Id !== action.payload.sectionId) return section;
              return {
                ...section,
                TimesheetDetails: section.TimesheetDetails.filter((timesheet) => timesheet.Id !== action.payload.id)
              };
            })
          }
        };
      }

      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          TimesheetDetails: state.data?.TimesheetDetails.filter((timesheet) => timesheet.Id !== action.payload.id)
        }
      };
    case types.DELETE_TIMESHEET_SECTION_FULFILLED:
      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          TimesheetSections: state.data.TimesheetSections.filter((section) => section.Id !== action.payload)
        }
      };
    case types.RESTORE_DELETED_TIMESHEET_LINE:
      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          TimesheetDetails: [...state.data.TimesheetDetails, action.payload.line]
        }
      };
    case types.CHECK_UNCHECK:
      return { ...state, pending: false, error: '', data: { ...state.data, sheetRows: action.payload } };
    case types.SAVE_SUCCESS:
      return { ...state, saveSuccess: action.payload };
    case types.SUBMIT_SUCCESS:
      return { ...state, submitSuccess: action.payload };
    case types.CREATE_NEW_TIMESHEET: {
      const { sectionId, isSplitApprovalFlow } = action.payload;
      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          ...(isSplitApprovalFlow
            ? {
                TimesheetSections: state?.data?.TimesheetSections.map((section) =>
                  section.Id === sectionId
                    ? {
                        ...section,
                        TimesheetDetails: [
                          ...section.TimesheetDetails,
                          {
                            Id: uuidv4(10),
                            Comment: '',
                            Location: '',
                            WorkType: { Id: 1, Name: 'Straight Time' },
                            TimesheetHoursByDates:
                              section?.TimesheetDetails?.[0]?.TimesheetHoursByDates?.map((item) => ({
                                Day: item.Day,
                                TotalHrs: 0
                              })) || []
                          }
                        ]
                      }
                    : section
                )
              }
            : {
                TimesheetDetails: [
                  ...state.data.TimesheetDetails,
                  {
                    Id: uuidv4(10),
                    Comment: '',
                    Location: '',
                    WorkType: { Id: 1, Name: 'Straight Time' },
                    TimesheetHoursByDates: state.data?.TimesheetDetails[0].TimesheetHoursByDates.map((item) => ({
                      Day: item.Day,
                      TotalHrs: 0
                    }))
                  }
                ]
              })
        }
      };
    }
    case types.ADD_NEW_SECTION: {
      const hoursTemplate =
        state.data?.TimesheetSections?.[0]?.TimesheetDetails?.[0]?.TimesheetHoursByDates?.map((item) => ({
          Day: item.Day,
          TotalHrs: 0
        })) || [];

      const perdiemTemplate =
        state.data?.TimesheetSections?.[0]?.TimesheetPerDiem?.map((item) => ({
          IsPerDiem: false,
          Date: item.Date
        })) || [];

      return {
        ...state,
        pending: false,
        error: '',
        data: {
          ...state.data,
          TimesheetSections: [
            ...state.data.TimesheetSections,
            {
              Id: uuidv4(),
              TimesheetDetails: [
                {
                  Id: uuidv4(),
                  Comment: '',
                  Location: '',
                  WorkType: { Id: 1, Name: 'Straight Time' },
                  TimesheetHoursByDates: hoursTemplate
                }
              ],
              TimesheetPerDiem: perdiemTemplate
            }
          ]
        }
      };
    }
    case types.GET_PENDING_TANDE_COUNTS_FULFILLED:
      return {
        ...state,
        pending: false,
        error: '',
        tandecounts: action.payload,
        isTimesheetEmpty:
          action.payload?.PendingTimesheetCount === 0 &&
          action.payload?.RejectedTimesheetCount === 0 &&
          action.payload?.ApprovedTimesheetCount === 0 &&
          action.payload?.ProcessedTimesheetCount === 0,
        isExpenseEmpty:
          action.payload?.PendingClaimsCount === 0 &&
          action.payload?.RejectedClaimsCount === 0 &&
          action.payload?.ApprovedClaimsCount === 0 &&
          action.payload?.ProcessedClaimsCount === 0,
        hasTimesheetPendingCount: action.payload?.PendingTimesheetCount === 0,
        hasExpensePendingCount: action.payload?.PendingClaimsCount === 0
      };

    case types.GET_TANDE_COUNTS_CONTRACTOR_FULFILLED:
      return {
        ...state,
        pending: false,
        error: '',
        contractortandecounts: action.payload,
        isTimesheetEmpty: Object.values(action.payload.Timesheet).every((item) => item === 0),
        isExpenseEmpty: Object.values(action.payload.Expense).every((item) => item === 0),
        hasTimesheetPendingCount: action.payload?.PendingTimesheetCount === 0,
        hasExpensePendingCount: action.payload?.PendingClaimsCount === 0
      };

    case types.GET_TIMESHEET_APPROVER_REQUEST_PENDING:
      return {
        ...state,
        pending: true,
        error: ''
      };
    case types.GET_TIMESHEET_APPROVER_REQUEST_FULFILLED:
      return {
        ...state,
        pending: false,
        error: '',
        notifications: state?.notifications?.map((notification) =>
          notification.type === action.payload.notificationType
            ? { ...notification, data: action.payload.data }
            : notification
        )
      };
    case types.GET_TIMESHEET_APPROVER_REQUEST_REJECTED:
      return {
        ...state,
        pending: false,
        error: action.payload
      };
    case types.CLEAR_TIMESHEET_DATA:
      return {
        ...state,
        pending: false,
        commentPending: false,
        error: '',
        data: null
      };
    case types.CHANGE_STATUS:
      return {
        ...state,
        data: {
          ...state.data,
          Status: {
            ...state.data.Status,
            Status: action.payload.status
          },
          TimesheetSections: state.data.TimesheetSections.map((section) => {
            if (!action.payload.sectionId || section.Id === action.payload.sectionId) {
              return {
                ...section,
                Status: {
                  ...section.Status,
                  Status: action.payload.status
                }
              };
            }

            return section;
          })
        }
      };
    case types.CHANGE_PERDIEM:
      if (action.payload.IsSplitApprovalFlow) {
        return {
          ...state,
          data: {
            ...state.data,
            TimesheetSections: state.data.TimesheetSections.map((section) => {
              if (section.Id !== action.payload.SectionId) return section;
              return {
                ...section,
                TimesheetPerDiem: action.payload.isMobile
                  ? action.payload.Data
                  : section.TimesheetPerDiem.map((currentPerDiem) =>
                      dayjs(currentPerDiem.Date).isSame(dayjs(action.payload.Data.Date), 'day')
                        ? { ...currentPerDiem, ...action.payload.Data }
                        : currentPerDiem
                    )
              };
            })
          }
        };
      }

      if (action.payload.isMobile) {
        return {
          ...state,
          data: {
            ...state.data,
            TimesheetPerDiem: action.payload.Data
          }
        };
      }

      return {
        ...state,
        data: {
          ...state.data,
          TimesheetPerDiem: state.data.TimesheetPerDiem.map((currentPerDiem) =>
            dayjs(currentPerDiem.Date).isSame(dayjs(action.payload.Data.Date), 'day')
              ? { ...currentPerDiem, ...action.payload.Data }
              : currentPerDiem
          )
        }
      };
    case types.GET_TIMESHEET_VALIDATION_FULFILLED:
      return {
        ...state,
        validatePending: false,
        validate: {
          timesheet: action.payload.timesheet,
          expense: action.payload.expense
        }
      };
    case types.GET_TIMESHEET_VALIDATION_PENDING:
      return {
        ...state,
        validatePending: true,
        validate: {
          timesheet: {
            IsValidAdminSettings: true,
            IsValidPlacementStatus: true
          },
          expense: {
            IsValidAdminSettings: true,
            IsValidPlacementStatus: true
          }
        }
      };
    case types.GET_TIMESHEET_VALIDATION_REJECTED:
      return {
        ...state,
        validatePending: false,
        validate: {
          timesheet: {
            IsValidAdminSettings: true,
            IsValidPlacementStatus: true
          },
          expense: {
            IsValidAdminSettings: true,
            IsValidPlacementStatus: true
          }
        }
      };
    case types.SAVE_COMMENT_PENDING:
      return {
        ...state,
        commentPending: true
      };
    case types.SAVE_COMMENT_REJECTED:
      return {
        ...state,
        commentPending: false
      };
    case types.SAVE_COMMENT_FULFILLED: {
      const newReason = {
        ActionTaken: 'Pending',
        Id: action.payload.Id,
        Reason: action.payload.Reason,
        CreatedDate: dayjs().toString(),
        User: {
          FirstName: localStorage.getItem('AirswiftFirstName'),
          LastName: localStorage.getItem('AirswiftLastName')
        }
      };

      if (action.payload.IsSplitApprovalFlow) {
        return {
          ...state,
          commentPending: false,
          data: {
            ...state.data,
            TimesheetSections: state.data.TimesheetSections.map((section) => {
              if (section.Id === action.payload.TimesheetSectionId) {
                return {
                  ...section,
                  TimesheetRejectionReasons: [
                    ...(section.TimesheetRejectionReasons || []),
                    {
                      ...newReason,
                      TimesheetSectionId: action.payload.TimesheetSectionId
                    }
                  ]
                };
              }
              return section;
            })
          }
        };
      }

      return {
        ...state,
        commentPending: false,
        data: {
          ...state.data,
          TimesheetRejectionReasons: [...(state.data.TimesheetRejectionReasons || []), newReason]
        }
      };
    }
    case types.UPDATE_COMMENT_PENDING:
      return {
        ...state,
        commentPending: true
      };
    case types.UPDATE_COMMENT_REJECTED:
      return {
        ...state,
        commentPending: false
      };
    case types.UPDATE_COMMENT_FULFILLED: {
      const updateComment = (comment) =>
        comment.Id === action.payload.commentId
          ? {
              ...comment,
              Reason: action.payload.comment,
              CreatedDate: dayjs().toString()
            }
          : comment;

      if (action.payload.IsSplitApprovalFlow) {
        return {
          ...state,
          commentPending: false,
          data: {
            ...state.data,
            TimesheetSections: state.data.TimesheetSections.map((section) => {
              if (section.Id === action.payload.TimesheetSectionId) {
                return {
                  ...section,
                  TimesheetRejectionReasons: (section.TimesheetRejectionReasons || []).map(updateComment)
                };
              }
              return section;
            })
          }
        };
      }

      return {
        ...state,
        commentPending: false,
        data: {
          ...state.data,
          TimesheetRejectionReasons: (state.data.TimesheetRejectionReasons || []).map(updateComment)
        }
      };
    }
    case types.DELETE_COMMENT_PENDING:
      return {
        ...state,
        commentPending: true
      };
    case types.DELETE_COMMENT_REJECTED:
      return {
        ...state,
        commentPending: false
      };
    case types.DELETE_COMMENT_FULFILLED:
      if (action.payload.IsSplitApprovalFlow) {
        return {
          ...state,
          commentPending: false,
          data: {
            ...state.data,
            TimesheetSections: state.data.TimesheetSections.map((section) => {
              if (section.Id === action.payload.SectionId) {
                return {
                  ...section,
                  TimesheetRejectionReasons: (section.TimesheetRejectionReasons || []).filter(
                    (comment) => comment.Id !== action.payload.commentId
                  )
                };
              }
              return section;
            })
          }
        };
      }

      return {
        ...state,
        commentPending: false,
        data: {
          ...state.data,
          TimesheetRejectionReasons: state.data.TimesheetRejectionReasons.filter(
            (comment) => comment.Id !== action.payload
          )
        }
      };
    case types.SET_SELECTED_SECTION:
      return {
        ...state,
        selectedSectionData: action.payload
      };
    default:
      return state;
  }
}
